import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import {
  Outlet,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import ContentHeader from "components/ContentHeader/ContentHeader";
import {
  ROUTES,
  BASE_PATHS,
  SUB_PATHS,
} from "app/utils/constants/routeConstants";
import useTags from "app/hooks/useTags";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import FilterSection from "./FilterSection";
import TagChips from "./TagChips";
import KeyOperatorChips from "./KeyOperatorChips";
import ProblemsTable from "./ProblemsTable";
import useFetchProblems from "app/hooks/useFetchProblems";
import PaymentSuccessDialog from "components/PaymentSuccessDialog";
import { OPERATOR_LABELS } from "app/utils/constants/constants";
import useMetaTitle from "app/hooks/useMetaTitle";

const ProblemListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tags } = useTags();
  const isAdmin = useAdmin();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogStatus, setPaymentDialogStatus] = useState(null);
  const [paymentDialogPlanName, setPaymentDialogPlanName] = useState("");

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const paymentStatusFromUrl = query.get("paymentStatus");
    const planNameFromUrl = query.get("planName");

    if (paymentStatusFromUrl && planNameFromUrl) {
      setPaymentDialogStatus(paymentStatusFromUrl);
      setPaymentDialogPlanName(planNameFromUrl);
      setShowPaymentDialog(true);
      navigate({ pathname: location.pathname }, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  const handleDialogClose = () => {
    setShowPaymentDialog(false);
    setPaymentDialogStatus(null);
    setPaymentDialogPlanName("");
  };

  // Modified onClose handler to prevent closing on backdrop click
  const handleDialogCloseOnlyOnContinue = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleDialogClose();
  };

  // Function to redirect to upgrade page
  const redirectToUpgrade = () => {
    navigate(ROUTES.UPGRADE);
  };

  // Determine if in admin context
  const isAdminContext = location.pathname.startsWith(
    BASE_PATHS.ADMIN_PROBLEMS
  );

  useMetaTitle({
    title: isAdminContext ? "Admin Problems" : "Algos And Ds",
  });

  // Configure based on admin context
  const addRoute = isAdminContext
    ? ROUTES.ADMIN_PROBLEMS_ADD
    : BASE_PATHS.ALGOS_AND_DS;
  const detailRouteBase = isAdminContext
    ? ROUTES.ADMIN_PROBLEMS
    : BASE_PATHS.ALGOS_AND_DS;
  const editRouteBase = isAdmin
    ? `${BASE_PATHS.ADMIN_PROBLEMS}/${SUB_PATHS.UPDATE_PROBLEM}`
    : undefined;

  // State from search params
  const rowsPerPage = Number(searchParams.get("size")) || 25;
  const page = Number(searchParams.get("page")) || 1;
  const selectedTag = searchParams.get("Tags")
    ? searchParams.get("Tags").split(",")
    : [];
  const searchQuery = searchParams.get("Title") || "";
  const sortParam = searchParams.get("sort") || "id,asc";
  const [sortField, sortOrder] = sortParam.split(",");
  const initialKeysWithOperatorsFromParams = [];
  for (const [key, value] of searchParams.entries()) {
    const [field, operator] = key.split(".");
    if (operator && Object.keys(OPERATOR_LABELS).includes(operator)) {
      initialKeysWithOperatorsFromParams.push({
        key: field,
        operator,
        searchTerm: value,
      });
    }
  }
  // Add ForPaidOnly and ProblemVideos from URL for non-admin context
  const forPaidOnlyFromUrl = searchParams.get("ForPaidOnly");
  if (forPaidOnlyFromUrl && !isAdminContext) {
    initialKeysWithOperatorsFromParams.push({
      key: "forPaidOnly",
      operator: "equals",
      searchTerm: forPaidOnlyFromUrl,
    });
  }
  const problemVideosFromUrl = searchParams.get("ProblemVideos");
  if (problemVideosFromUrl && !isAdminContext) {
    initialKeysWithOperatorsFromParams.push({
      key: "problemVideos",
      operator: "specified",
      searchTerm: problemVideosFromUrl,
    });
  }

  // Component state
  const [selectedTags, setSelectedTags] = useState(
    Array.isArray(selectedTag) ? selectedTag : []
  );
  const [search, setSearch] = useState(searchQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [rows, setRows] = useState(rowsPerPage);
  const [currentPage, setCurrentPage] = useState(page);
  const [order, setOrder] = useState(sortOrder || "asc");
  const [orderBy, setOrderBy] = useState(sortField || "id");
  const [keysWithOperators, setKeysWithOperators] = useState(
    Array.isArray(initialKeysWithOperatorsFromParams)
      ? initialKeysWithOperatorsFromParams
      : []
  );
  const [currentOperator, setCurrentOperator] = useState("equals");
  const [editingKey, setEditingKey] = useState(null);

  // Determine if fetching should be skipped
  const skipFetch = false; // Always fetch, even with no criteria

  // Fetch data
  const { problems, totalCount, isLoading, error } = useFetchProblems(
    currentPage,
    rows,
    orderBy,
    order,
    selectedTags,
    keysWithOperators,
    isAdminContext && keysWithOperators.length > 0 ? "" : debouncedSearch,
    skipFetch,
    !isAdminContext
  );

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Update search params
  const stableSelectedTags = useMemo(() => selectedTags, [selectedTags]);
  const stableKeysWithOperators = useMemo(
    () => keysWithOperators,
    [keysWithOperators]
  );
  useEffect(() => {
    const newParams = new URLSearchParams();
    newParams.set("size", rows.toString());
    newParams.set("page", currentPage.toString());
    newParams.set("sort", `${orderBy},${order}`);

    if (stableSelectedTags.length > 0) {
      newParams.set("Tags", stableSelectedTags.join(","));
    }
    if (
      debouncedSearch &&
      !(isAdminContext && stableKeysWithOperators.length > 0)
    ) {
      newParams.set("Title", debouncedSearch);
    }

    // Add filters to URL
    stableKeysWithOperators.forEach(({ key, operator, searchTerm }) => {
      if (searchTerm) {
        if (isAdminContext) {
          // Admin context: use key.operator=searchTerm format
          newParams.set(`${key}.${operator}`, searchTerm);
        } else {
          // Non-admin context: use simple key=value for forPaidOnly and problemVideos
          if (key === "forPaidOnly" || key === "problemVideos") {
            newParams.set(
              key === "forPaidOnly" ? "Free to execute" : "Videos",
              searchTerm
            );
          }
        }
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [
    stableSelectedTags,
    rows,
    debouncedSearch,
    currentPage,
    order,
    orderBy,
    stableKeysWithOperators,
    isAdminContext,
    setSearchParams,
  ]);

  // Handlers
  const handleTagChange = (newTags) => {
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  const handleKeyChange = (newKeys) => {
    const currentKeys = keysWithOperators.map(({ key }) => key);
    const addedKeys = newKeys
      .filter((key) => !currentKeys.includes(key))
      .map((key) => ({ key, operator: currentOperator, searchTerm: "" }));
    const updatedKeysWithOperators = [
      ...keysWithOperators.filter(({ key }) => newKeys.includes(key)),
      ...addedKeys,
    ];
    setKeysWithOperators(updatedKeysWithOperators);
    setCurrentPage(1);
    if (!editingKey && addedKeys.length > 0) {
      setEditingKey(addedKeys[0].key);
      setSearch("");
    }
  };

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch);
    if (isAdminContext && editingKey) {
      setKeysWithOperators((prev) =>
        prev.map((item) =>
          item.key === editingKey ? { ...item, searchTerm: newSearch } : item
        )
      );
    }
    setCurrentPage(1);
  };

  const handleReset = (event) => {
    setSelectedTags([]);
    setSearch("");
    setDebouncedSearch("");
    setRows(25);
    setCurrentPage(1);
    setKeysWithOperators([]);
    setCurrentOperator("equals");
    setEditingKey(null);
    setOrder("asc");
    setOrderBy("id");
    setSearchParams(new URLSearchParams());
  };

  const handleDeleteTag = (tag) => {
    const newTags = selectedTags.filter((t) => t !== tag);
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  const handleDeleteKey = (keyToDelete) => {
    setKeysWithOperators((prev) =>
      prev.filter(({ key }) => key !== keyToDelete)
    );
    setCurrentPage(1);
    if (editingKey === keyToDelete) {
      setEditingKey(null);
      setSearch("");
    }
  };

  const handleOperatorChangeForKey = (key, newOperator) => {
    setKeysWithOperators((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, operator: newOperator } : item
      )
    );
    setCurrentPage(1);
  };

  const handleEditFilter = (key, searchTerm) => {
    setEditingKey(key);
    setSearch(searchTerm || "");
    setCurrentPage(1);
  };

  const handleRequestSort = (columnId) => {
    const apiField =
      columnId === "title"
        ? "defaultTitle"
        : columnId === "tags"
        ? "problemTags"
        : columnId;
    setOrderBy((prevSortBy) => {
      if (prevSortBy === apiField) {
        setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        return prevSortBy;
      } else {
        setOrder("asc");
        return apiField;
      }
    });
    setCurrentPage(1);
  };

  const handleView = (row) => {
    const isAdminRoute = detailRouteBase === ROUTES.ADMIN_PROBLEMS;
    const identifier = isAdmin && !isAdminRoute ? row.name : row.id;
    navigate(`${detailRouteBase}/${identifier}`);
  };

  const handleEdit = (row) => {
    const isAdminRoute = detailRouteBase === ROUTES.ADMIN_PROBLEMS;
    const identifier = isAdmin && !isAdminRoute ? row.name : row.id;
    navigate(`${editRouteBase}/${identifier}`);
  };

  const handleActionsDelete = (problemId) => {
    alert(`Delete problem with ID: ${problemId}`);
  };

  const handleChangeRowsPerPage = (event) => {
    setRows(+event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleAddContent = useCallback(() => {
    navigate(ROUTES.ADMIN_PROBLEMS_ADD);
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
    setTimeout(() => {
      if (window.location.pathname) {
        navigate(ROUTES.USER_HOME);
      }
    }, 100);
  }, [navigate]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        case "add":
          handleAddContent();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleAddContent, handleBack]
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      <JumboCardQuick
        title={
          <ContentHeader
            isAdmin={isAdmin}
            onAction={handleAction}
            allowedActions={isAdmin ? ["add", "back"] : ["back"]}
            showLeftTabs={true}
            actionLabels={{ add: "Add a problem" }}
          />
        }
        headerSx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiCardHeader-action": { my: -0.75 },
        }}
        wrapperSx={{ p: 0 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "& .MuiCardContent-root:last-child": { paddingBottom: 0 },
          "& .MuiCardContent-root": {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
          },
        }}
      >
        <FilterSection
          isAdmin={isAdmin}
          isAdminContext={isAdminContext}
          tags={tags}
          selectedTags={selectedTags}
          keysWithOperators={keysWithOperators}
          setKeysWithOperators={setKeysWithOperators}
          setCurrentPage={setCurrentPage}
          search={search}
          rows={rows}
          onTagChange={handleTagChange}
          onKeyChange={handleKeyChange}
          onSearchChange={handleSearchChange}
          onReset={handleReset}
          onRowsPerPageChange={handleChangeRowsPerPage}
          addRoute={addRoute}
          navigate={navigate}
          problems={problems}
        />
        {isAdminContext && (
          <KeyOperatorChips
            keysWithOperators={keysWithOperators}
            editingKey={editingKey}
            onDeleteKey={handleDeleteKey}
            onOperatorChange={handleOperatorChangeForKey}
            onEditFilter={handleEditFilter}
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <TagChips selectedTags={selectedTags} onDeleteTag={handleDeleteTag} />
        </Box>
        <ProblemsTable
          isAdmin={isAdmin}
          displayData={problems}
          displayCount={totalCount}
          loading={isLoading}
          error={error}
          rows={rows}
          currentPage={currentPage - 1}
          order={order}
          orderBy={orderBy}
          detailRouteBase={detailRouteBase}
          onPageChange={handlePageChange}
          onRequestSort={handleRequestSort}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleActionsDelete}
        />
        <PaymentSuccessDialog
          open={showPaymentDialog}
          onClose={handleDialogCloseOnlyOnContinue}
          paymentStatus={paymentDialogStatus}
          planName={paymentDialogPlanName}
          redirectToUpgrade={redirectToUpgrade}
          disableEscapeKeyDown={true}
        />
      </JumboCardQuick>
      <Outlet />
    </Box>
  );
};

export default ProblemListPage;
